import { ApolloProvider } from '@apollo/client'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import { I18nextProvider, useTranslation } from 'react-i18next'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router'
import '@moia-dev/pace-core-web/moia/dark.css'
import '@moia-dev/pace-core-web/moia/light.css'
import { IncludeTheme } from '@moia-dev/pace-web'
import '@moia-dev/pace-web/style.css'
import {
  AuthenticatedAppView,
  getApolloClient,
} from '@backoffice-frontend/authenticated-app-view'
import {
  appConfig,
  configureI18n,
  DEFAULT_LANGUAGE,
  LANGUAGES,
  RefContextProvider,
  Routes,
} from '@backoffice-frontend/common'
import { useAppearance } from '@backoffice-frontend/dark-mode'
import { ENV, Environment } from '@backoffice-frontend/environment'
import {
  DomainMigrationRedirect,
  PublicAppView,
  ShareDomainRedirect,
} from '@backoffice-frontend/login-view'
import {
  CssBaseline,
  createTheme,
  darkThemeOptions,
  GlobalStyles,
  MoiaLogo,
  moiaThemeOptions,
  StyledEngineProvider,
  SuspenseWrapper,
  ThemeProvider,
} from '@backoffice-frontend/patterns'
import { Layout } from '../Layout'
import { loadSharedAreaTranslations } from '../sharedAreaTranslations'
import { loadSharedLibTranslations } from '../sharedLibTranslations'
import { loadTranslations } from './locales'
import { useNavigationSections } from './useNavigationSections'

// configure app
appConfig()

const languageDetectionOptions = [Environment.local, Environment.test].includes(
  ENV,
)
  ? { order: ['localStorage'] }
  : {}

const i18n = configureI18n({
  supportedLngs: LANGUAGES,
  fallbackLng: DEFAULT_LANGUAGE,
  detection: languageDetectionOptions,
})

loadSharedLibTranslations()
loadSharedAreaTranslations()
loadTranslations()

// theme has to be defined in app root as long as we use makeStyles
//
const lightTheme = createTheme(moiaThemeOptions)
const darkTheme = createTheme(darkThemeOptions)
const apolloClient = getApolloClient('backoffice-frontend', [])

export const App = () => {
  const { darkMode } = useAppearance()
  const { t } = useTranslation()

  return (
    <>
      <IncludeTheme theme={darkMode ? 'dark' : 'light'} />
      <I18nextProvider i18n={i18n}>
        <StyledEngineProvider>
          <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
            <CssBaseline />
            <GlobalStyles darkMode={darkMode} />
            <NuqsAdapter>
              <ShareDomainRedirect>
                <DomainMigrationRedirect>
                  <RouterProvider
                    router={createBrowserRouter(
                      createRoutesFromElements(
                        <Route element={<Layout />}>
                          <Route
                            path={Routes.Auth.Login.path}
                            element={
                              <>
                                <title>{t('backofficeApp_tab_title')}</title>
                                <div
                                  css={{
                                    minHeight: '100vh',
                                    backgroundSize: 'cover',
                                    alignItems: 'flex-start',
                                    justifyItems: 'center',
                                    backgroundImage: `url(/assets/images/login.png)`,
                                    display: 'grid',
                                  }}
                                >
                                  <MoiaLogo
                                    css={theme => ({
                                      width: '160px',
                                      paddingTop: theme.spacing(6),
                                      color: theme.palette.common.white,
                                    })}
                                  />
                                  <PublicAppView
                                    title={t('backofficeApp_login_title')}
                                  />
                                </div>
                              </>
                            }
                          />
                          <Route
                            path="/*"
                            element={
                              <ApolloProvider client={apolloClient}>
                                <SuspenseWrapper>
                                  <RefContextProvider>
                                    <AuthenticatedAppView
                                      useNavigationSections={
                                        useNavigationSections
                                      }
                                      logo={
                                        <MoiaLogo css={{ maxHeight: 28 }} />
                                      }
                                      trackRecentlyVisitedAreas
                                      wrongSubdomainTitle={t(
                                        'backofficeApp_login_title',
                                      )}
                                    />
                                  </RefContextProvider>
                                </SuspenseWrapper>
                              </ApolloProvider>
                            }
                          />
                        </Route>,
                      ),
                    )}
                  />
                </DomainMigrationRedirect>
              </ShareDomainRedirect>
            </NuqsAdapter>
          </ThemeProvider>
        </StyledEngineProvider>
      </I18nextProvider>
    </>
  )
}
